import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  ExternalLink,
  Compass,
  Heart,
  Target,
  Brain,
  Award,
  TrendingUp,
  GraduationCap,
  Users,
  ShieldCheck,
  BookOpen,
  Sparkles,
  Calendar,
  Plus,
  Minus,
} from "lucide-react";
import { APPLICATION_LINK_PROPS } from "../config/applicationForm";
import { mailtoLink } from "../config/contacts";
import { MYI_LOGO_FULL, MYI_ICON } from "../config/myiBrand";
import RelatedServices from "../components/RelatedServices";

/* Cinematic mentorship image (mentor in conversation, warm low light) */
const HERO_IMG =
  "https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1600&q=80";
const CLOSING_IMG =
  "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?auto=format&fit=crop&w=1600&q=80";
const PROCESS_IMG =
  "https://images.unsplash.com/photo-1517960413843-0aee8e2b3285?auto=format&fit=crop&w=1400&q=80";
const MENTOR_IMG =
  "https://customer-assets.emergentagent.com/job_hero-design-6/artifacts/oiuyhizp_fad0e5ea-1712-413b-9f1d-c05784213e9c.png";

const APPLY_PROPS = APPLICATION_LINK_PROPS;
const DISCOVERY_MAILTO = mailtoLink("booking", "Discovery Call — Youth Mentorship & Guidance");

/* ---------- Section primitives ---------- */
const Eyebrow = ({ children }) => (
  <div className="text-[10.5px] tracking-[0.34em] uppercase text-gold">
    {children}
  </div>
);

const SectionTitle = ({ children, className = "" }) => (
  <h2
    className={`font-serif text-white text-[26px] sm:text-[32px] lg:text-[38px] leading-[1.08] tracking-[-0.012em] ${className}`}
  >
    {children}
  </h2>
);

const SectionWrap = ({ id, children, className = "" }) => (
  <section id={id} className={`relative border-b border-white/5 ${className}`}>
    <div className="max-w-[1180px] mx-auto px-6 lg:px-12 py-16 lg:py-20">
      {children}
    </div>
  </section>
);

/* ---------- Data ---------- */
const CHALLENGES = [
  "Identity confusion",
  "Social-media pressure",
  "Lack of direction",
  "Low confidence",
  "Academic stress",
  "Weak habits",
  "Disconnect from faith",
];

const PILLARS = [
  {
    Icon: GraduationCap,
    title: "20+ Years in Education",
    body: "Classroom teacher, school leader, and mentor across continents.",
  },
  {
    Icon: Users,
    title: "Proven Experience Mentoring Youth",
    body: "Hundreds of students guided across diverse backgrounds and stages.",
  },
  {
    Icon: ShieldCheck,
    title: "Character-Focused Development",
    body: "Building integrity, confidence and responsibility — not just resumés.",
  },
  {
    Icon: BookOpen,
    title: "Islamic Framework",
    body: "Guidance rooted in timeless principles and the Murabbi tradition.",
  },
  {
    Icon: Target,
    title: "Practical Goal Setting",
    body: "Real conversations, actionable strategies, lasting growth.",
  },
  {
    Icon: TrendingUp,
    title: "Long-Term Growth & Accountability",
    body: "More than short-term motivation — we build for life.",
  },
];

const AREAS = [
  { Icon: Compass, title: "Identity & Purpose", body: "Discover who you are and why you're here." },
  { Icon: Heart, title: "Faith & Character", body: "Strengthen your faith and build noble character." },
  { Icon: Target, title: "Discipline & Habits", body: "Build routines and habits that create lasting change." },
  { Icon: Brain, title: "Emotional Intelligence", body: "Manage emotions, improve communication, build resilience." },
  { Icon: Award, title: "Leadership & Service", body: "Develop confidence, lead with character, serve with impact." },
  { Icon: Sparkles, title: "Goals & Achievement", body: "Set meaningful goals and take consistent action to achieve them." },
];

const PROCESS = [
  { n: "01", t: "Initial Assessment", d: "We get to know you — your story, struggles and goals." },
  { n: "02", t: "Goal Setting", d: "Clear, meaningful goals tailored to your growth." },
  { n: "03", t: "Regular Sessions", d: "One-on-one meetings (virtual or in-person)." },
  { n: "04", t: "Action & Accountability", d: "Custom action plans and consistent follow-up." },
  { n: "05", t: "Progress Tracking", d: "Tracking progress and celebrating wins together." },
  { n: "06", t: "Parent Communication", d: "Parents are kept informed and involved throughout." },
];

const COHORTS = [
  { t: "Middle School Students", d: "Building foundations of identity and faith." },
  { t: "High School Students", d: "Navigating adolescence with purpose." },
  { t: "University Students", d: "Developing independence and leadership." },
  { t: "Young Professionals", d: "Growing in character and contribution." },
];

const CHALLENGE_CHIPS = [
  "Confidence",
  "Motivation",
  "Leadership",
  "Discipline",
  "Organization",
  "Faith Development",
  "Peer Pressure",
  "Purpose",
];

const FAQ = [
  {
    q: "Is this therapy?",
    a:
      "No. Mentorship complements but never replaces therapy. If a student needs clinical care, we will say so directly and help the family find appropriate support.",
  },
  {
    q: "Is this counseling?",
    a:
      "No. Counseling addresses specific psychological challenges. Mentorship focuses on formation — character, faith, habits, leadership and purpose over time.",
  },
  {
    q: "How often do students meet?",
    a:
      "Typically bi-weekly one-hour sessions, with check-ins between. Frequency is calibrated to each student's stage and goals.",
  },
  {
    q: "How are parents involved?",
    a:
      "Parents receive regular communication and progress notes. We honour the trust between mentor and mentee while keeping families informed and aligned.",
  },
  {
    q: "Who is this designed for?",
    a:
      "Middle-school students through young professionals — boys and young men seeking guidance in faith, character and direction. Long-term, not short-term.",
  },
];

const TESTIMONIALS = [
  {
    quote: "—",
    author: "Parent of High School Student",
    placeholder: true,
  },
  {
    quote: "—",
    author: "High School Mentee",
    placeholder: true,
  },
  {
    quote: "—",
    author: "University Student",
    placeholder: true,
  },
];

/* ---------- Components ---------- */
const PillarCard = ({ Icon, title, body }) => (
  <div className="group relative border border-white/10 bg-ink-soft/40 hover:border-azure/40 transition-colors duration-500 p-6 lg:p-7 h-full flex flex-col">
    <span
      aria-hidden="true"
      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
      style={{
        background:
          "radial-gradient(ellipse 80% 100% at 0% 0%, rgba(74,143,229,0.10), transparent 70%)",
      }}
    />
    <div className="relative">
      <div className="w-10 h-10 rounded-full border border-azure-soft/40 flex items-center justify-center text-azure-soft">
        <Icon className="w-4 h-4" strokeWidth={1.6} />
      </div>
      <h3 className="mt-6 font-serif text-white text-[17px] lg:text-[18px] leading-[1.25] tracking-[-0.01em] group-hover:text-azure-soft transition-colors duration-500">
        {title}
      </h3>
      <p className="mt-3 text-white/65 text-[14px] leading-[1.8]">{body}</p>
    </div>
  </div>
);

const AreaCard = ({ Icon, title, body }) => (
  <div className="group relative border border-white/10 bg-ink-soft/30 p-6 lg:p-7 hover:border-gold/40 transition-colors duration-500 text-center">
    <div className="mx-auto w-12 h-12 rounded-full border border-gold/40 flex items-center justify-center text-gold transition-transform duration-500 group-hover:scale-105">
      <Icon className="w-4.5 h-4.5" strokeWidth={1.6} />
    </div>
    <h4 className="mt-5 font-serif text-white text-[17px] leading-[1.25] tracking-[-0.01em]">
      {title}
    </h4>
    <p className="mt-3 text-white/60 text-[13.5px] leading-[1.8]">{body}</p>
  </div>
);

const FaqItem = ({ q, a, isOpen, onToggle, testId }) => (
  <div className="border-b border-white/10">
    <button
      type="button"
      onClick={onToggle}
      data-testid={testId}
      className="w-full flex items-center justify-between text-left py-5 group"
    >
      <span className="font-serif text-white text-[17px] lg:text-[19px] tracking-[-0.005em] group-hover:text-azure-soft transition-colors">
        {q}
      </span>
      <span className="ml-6 shrink-0 w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/60 group-hover:text-gold group-hover:border-gold transition-colors">
        {isOpen ? (
          <Minus className="w-4 h-4" strokeWidth={1.6} />
        ) : (
          <Plus className="w-4 h-4" strokeWidth={1.6} />
        )}
      </span>
    </button>
    {isOpen && (
      <div className="pb-6 pr-12 text-white/65 text-[14.5px] leading-[1.85]">{a}</div>
    )}
  </div>
);

/* ---------- Page ---------- */
const YouthMentorshipGuidance = () => {
  const [openFaq, setOpenFaq] = useState(0);

  /* Swap favicon to the MYI mark while on this page. */
  useEffect(() => {
    const link =
      document.querySelector("link[rel~='icon']") ||
      document.createElement("link");
    const previous = link.href;
    link.rel = "icon";
    link.href = MYI_ICON;
    if (!link.parentNode) document.head.appendChild(link);
    return () => {
      link.href = previous;
    };
  }, []);

  return (
    <main data-testid="youth-mentorship-guidance-page" className="bg-ink min-h-screen">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Mentor in conversation with a young man"
            className="w-full h-full object-cover opacity-55"
          />
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(90deg, rgba(8,13,28,0.94) 0%, rgba(8,13,28,0.78) 45%, rgba(8,13,28,0.55) 100%), radial-gradient(ellipse 55% 60% at 80% 20%, rgba(74,143,229,0.16), transparent 65%)",
            }}
          />
        </div>

        <div className="relative max-w-[1180px] mx-auto px-6 lg:px-12 pt-28 lg:pt-32 pb-20 lg:pb-24">
          <Link
            to="/consulting"
            data-testid="ymg-back-link"
            className="group inline-flex items-center gap-3 text-[10.5px] tracking-[0.34em] uppercase text-white/55 hover:text-azure-soft transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5 transition-transform duration-500 group-hover:-translate-x-1" />
            Back to Formation &amp; Initiatives
          </Link>

          {/* MYI hero logo */}
          <div className="mt-14 lg:mt-16">
            <img
              src={MYI_LOGO_FULL}
              alt="The Murabbi Youth Initiative"
              data-testid="myi-hero-logo"
              className="block w-[240px] sm:w-[280px] lg:w-[320px] h-auto"
              style={{ imageRendering: "auto" }}
            />
          </div>

          <div className="mt-10 flex items-center gap-4">
            <span className="text-[11px] tracking-[0.34em] uppercase text-azure-soft">
              02 — Youth Mentorship &amp; Guidance
            </span>
            <span className="h-px w-12 bg-azure-soft/50" />
          </div>

          <h1 className="mt-7 font-serif text-white text-[34px] sm:text-[48px] lg:text-[64px] leading-[1.02] tracking-[-0.02em] max-w-3xl">
            Every Young Person Needs{" "}
            <span className="italic text-azure-soft">Guidance</span>.
          </h1>

          <div className="mt-8 h-px w-20 bg-gold" />

          <p className="mt-8 text-white/75 text-[16px] lg:text-[17px] leading-[1.9] max-w-2xl">
            Helping young Muslims develop confidence, discipline, faith, character,
            leadership and purpose through personalized mentorship rooted in the
            Murabbi tradition.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <a
              {...APPLY_PROPS}
              data-testid="ymg-hero-apply"
              className="group inline-flex items-center justify-center gap-3 bg-azure hover:bg-[#5C9BEC] text-white px-7 py-3.5 text-[11px] tracking-[0.32em] uppercase font-medium transition-all"
            >
              Apply For Mentorship
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <a
              href={DISCOVERY_MAILTO}
              data-testid="ymg-hero-discovery"
              className="group inline-flex items-center justify-center gap-3 border border-white/40 hover:border-gold hover:text-gold text-white px-7 py-3.5 text-[11px] tracking-[0.32em] uppercase font-medium transition-all"
            >
              Schedule A Discovery Call
              <Calendar className="w-3.5 h-3.5" strokeWidth={1.8} />
            </a>
          </div>
        </div>
      </section>

      {/* 2 — THE CHALLENGE */}
      <SectionWrap id="challenge">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-16 items-start">
          <div>
            <Eyebrow>The Challenge</Eyebrow>
            <SectionTitle className="mt-5">
              Growing up has{" "}
              <span className="italic text-azure-soft">never</span> been more
              complicated.
            </SectionTitle>
            <div className="mt-7 h-px w-12 bg-gold" />
            <p className="mt-7 text-white/70 text-[15.5px] leading-[1.9] max-w-md">
              From social-media pressure and identity confusion to academic stress
              and a lack of direction — today&apos;s youth face challenges previous
              generations never had to navigate.
            </p>
            <p className="mt-5 font-serif italic text-azure-soft text-[20px] lg:text-[22px] leading-[1.35] tracking-[-0.005em]">
              They don&apos;t just need advice.
              <br />
              They need guidance.
            </p>
          </div>

          <ul className="grid sm:grid-cols-2 gap-3">
            {CHALLENGES.map((c) => (
              <li
                key={c}
                className="border border-white/10 px-5 py-4 text-white/75 text-[14.5px] tracking-[-0.005em] hover:border-azure/40 transition-colors bg-ink-soft/30"
              >
                <span className="text-azure-soft mr-3">·</span>
                {c}
              </li>
            ))}
          </ul>
        </div>
      </SectionWrap>

      {/* 3 — A DIFFERENT KIND OF MENTORSHIP */}
      <SectionWrap id="different" className="bg-ink-deep/40">
        <div className="grid lg:grid-cols-[0.85fr_1.4fr] gap-10 lg:gap-16 items-start">
          <div>
            <Eyebrow>A Different Kind of Mentorship</Eyebrow>
            <SectionTitle className="mt-5">
              Not therapy. Not coaching.{" "}
              <span className="italic text-azure-soft">Mentorship.</span>
            </SectionTitle>
            <div className="mt-7 h-px w-12 bg-gold" />
            <p className="mt-7 text-white/70 text-[15.5px] leading-[1.9] max-w-md">
              This isn&apos;t therapy. This isn&apos;t counseling. This is mentorship
              rooted in two decades of experience, faith, and a proven track record
              of helping young people grow.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            {PILLARS.map((p) => (
              <PillarCard key={p.title} {...p} />
            ))}
          </div>
        </div>
      </SectionWrap>

      {/* 4 — SIX AREAS OF DEVELOPMENT */}
      <SectionWrap id="areas">
        <div className="text-center max-w-2xl mx-auto">
          <div className="flex items-center justify-center gap-3">
            <img
              src={MYI_ICON}
              alt=""
              aria-hidden="true"
              data-testid="myi-section-icon-areas"
              className="w-8 h-8 rounded-full"
            />
            <Eyebrow>The Six Areas of Development</Eyebrow>
          </div>
          <SectionTitle className="mt-5">
            A whole-person approach to{" "}
            <span className="italic text-azure-soft">growth</span>.
          </SectionTitle>
          <div className="mt-7 h-px w-12 bg-gold mx-auto" />
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {AREAS.map((a) => (
            <AreaCard key={a.title} {...a} />
          ))}
        </div>
      </SectionWrap>

      {/* MYI divider before program structure */}
      <div className="relative border-b border-white/5 bg-ink">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-12 py-12 lg:py-14 flex flex-col items-center gap-4">
          <span className="h-px w-16 bg-azure-soft/40" />
          <img
            src={MYI_LOGO_FULL}
            alt="The Murabbi Youth Initiative"
            data-testid="myi-section-divider"
            className="w-[200px] sm:w-[220px] h-auto opacity-90"
          />
          <span className="h-px w-16 bg-azure-soft/40" />
        </div>
      </div>

      {/* 5 — HOW MENTORSHIP WORKS */}
      <SectionWrap id="how" className="bg-ink-deep/40">
        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-start">
          <div>
            <Eyebrow>How Mentorship Works</Eyebrow>
            <SectionTitle className="mt-5">
              A clear, deliberate{" "}
              <span className="italic text-azure-soft">process</span>.
            </SectionTitle>
            <div className="mt-7 h-px w-12 bg-gold" />

            <ol className="mt-10 relative">
              <span
                aria-hidden="true"
                className="absolute left-[19px] top-2 bottom-2 w-px bg-azure-soft/25"
              />
              {PROCESS.map((step) => (
                <li key={step.n} className="relative pl-14 pb-7 last:pb-0">
                  <span className="absolute left-0 top-0 w-10 h-10 rounded-full border border-gold/50 bg-ink-deep text-gold font-serif text-[13px] tracking-[0.05em] flex items-center justify-center">
                    {step.n}
                  </span>
                  <div className="font-serif text-white text-[18px] leading-[1.2] tracking-[-0.005em]">
                    {step.t}
                  </div>
                  <p className="mt-2 text-white/60 text-[14px] leading-[1.8]">
                    {step.d}
                  </p>
                </li>
              ))}
            </ol>
          </div>

          <div className="relative">
            <div className="relative border border-white/10 overflow-hidden">
              <img
                src={PROCESS_IMG}
                alt="A mentor walking with a student at sunset"
                className="w-full h-full object-cover"
              />
              <div
                aria-hidden="true"
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(8,13,28,0.10) 0%, rgba(8,13,28,0.45) 100%)",
                }}
              />
            </div>
            <div className="mt-5 text-white/45 text-[11.5px] tracking-[0.18em] uppercase">
              Two walking — a quiet picture of the work.
            </div>
          </div>
        </div>
      </SectionWrap>

      {/* 6 — WHO THIS IS FOR */}
      <SectionWrap id="for">
        <div className="grid lg:grid-cols-[1fr_1fr] gap-10 lg:gap-16 items-start">
          <div>
            <Eyebrow>Who This Is For</Eyebrow>
            <SectionTitle className="mt-5">
              Boys and young men at four{" "}
              <span className="italic text-azure-soft">stages</span>.
            </SectionTitle>
            <div className="mt-7 h-px w-12 bg-gold" />
            <ul className="mt-10 space-y-5">
              {COHORTS.map((c) => (
                <li key={c.t} className="flex items-start gap-4">
                  <span className="mt-2 w-1.5 h-1.5 bg-gold shrink-0" />
                  <div>
                    <div className="font-serif text-white text-[18px] leading-[1.2] tracking-[-0.005em]">
                      {c.t}
                    </div>
                    <div className="text-white/60 text-[14px] leading-[1.8] mt-1">
                      {c.d}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <Eyebrow>Common Challenges We Help With</Eyebrow>
            <div className="mt-7 h-px w-12 bg-gold" />
            <div className="mt-8 flex flex-wrap gap-2.5">
              {CHALLENGE_CHIPS.map((c) => (
                <span
                  key={c}
                  className="border border-white/15 px-4 py-2 text-[11px] tracking-[0.22em] uppercase text-white/75 hover:border-azure/50 hover:text-azure-soft transition-colors"
                >
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>
      </SectionWrap>

      {/* 7 — ABOUT THE MENTOR */}
      <SectionWrap id="mentor" className="bg-ink-deep/40">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-16 items-center">
          <div className="relative border border-white/10 overflow-hidden">
            <img
              src={MENTOR_IMG}
              alt="Iyare Omoruyi — Educator, Speaker, Murabbi"
              className="w-full h-full object-cover"
            />
            <div
              aria-hidden="true"
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(8,13,28,0.05) 40%, rgba(8,13,28,0.55) 100%)",
              }}
            />
          </div>
          <div>
            <Eyebrow>About the Mentor</Eyebrow>
            <SectionTitle className="mt-5">
              Two decades of teaching, mentoring,{" "}
              <span className="italic text-azure-soft">forming</span>.
            </SectionTitle>
            <div className="mt-7 h-px w-12 bg-gold" />
            <p className="mt-7 text-white/75 text-[15.5px] leading-[1.9] max-w-xl">
              I have spent more than two decades helping young people grow in
              knowledge, confidence, character and faith. My work combines education,
              mentorship and the Murabbi tradition to help students become the people
              they were created to be.
            </p>
            <figure className="mt-9 relative">
              <span
                aria-hidden="true"
                className="absolute -left-2 -top-6 font-serif text-azure-soft/35 text-[72px] leading-none select-none pointer-events-none"
              >
                “
              </span>
              <blockquote className="font-serif italic text-white text-[20px] lg:text-[24px] leading-[1.35] tracking-[-0.005em] max-w-xl">
                The teachers who shaped me did not teach me lessons. They taught me
                how to become a person.
              </blockquote>
              <figcaption className="mt-5 text-[10.5px] tracking-[0.34em] uppercase text-white/50">
                — Iyare Omoruyi
              </figcaption>
            </figure>
          </div>
        </div>
      </SectionWrap>

      {/* 8 — FAQ */}
      <SectionWrap id="faq">
        <div className="grid lg:grid-cols-[0.85fr_1.6fr] gap-10 lg:gap-16">
          <div>
            <Eyebrow>Frequently Asked</Eyebrow>
            <SectionTitle className="mt-5">
              Questions parents{" "}
              <span className="italic text-azure-soft">often ask</span>.
            </SectionTitle>
            <div className="mt-7 h-px w-12 bg-gold" />
          </div>
          <div>
            {FAQ.map((f, idx) => (
              <FaqItem
                key={f.q}
                q={f.q}
                a={f.a}
                isOpen={openFaq === idx}
                onToggle={() => setOpenFaq(openFaq === idx ? -1 : idx)}
                testId={`ymg-faq-${idx}`}
              />
            ))}
          </div>
        </div>
      </SectionWrap>

      {/* 9 — TESTIMONIALS */}
      <SectionWrap id="testimonials" className="bg-ink-deep/40">
        <div className="text-center max-w-2xl mx-auto">
          <Eyebrow>What Parents &amp; Students Say</Eyebrow>
          <SectionTitle className="mt-5">
            Voices from{" "}
            <span className="italic text-azure-soft">the journey</span>.
          </SectionTitle>
          <div className="mt-7 h-px w-12 bg-gold mx-auto" />
          <p className="mt-7 text-white/55 text-[14px] leading-[1.85]">
            Testimonials from current parents, mentees and alumni — collected with
            care, shared with permission.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-5 lg:gap-6">
          {TESTIMONIALS.map((t, i) => (
            <figure
              key={i}
              className="relative border border-white/10 bg-ink-soft/30 p-7 lg:p-8 min-h-[230px] flex flex-col"
            >
              <span
                aria-hidden="true"
                className="font-serif text-azure-soft/40 text-[56px] leading-none select-none pointer-events-none"
              >
                “
              </span>
              <blockquote className="mt-2 font-serif italic text-white/55 text-[16px] lg:text-[17px] leading-[1.55] tracking-[-0.005em]">
                Coming soon.
              </blockquote>
              <figcaption className="mt-auto pt-6 text-[10.5px] tracking-[0.34em] uppercase text-white/45">
                — {t.author}
              </figcaption>
            </figure>
          ))}
        </div>
      </SectionWrap>

      {/* 10 — FINAL CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={CLOSING_IMG}
            alt="Two walking at sunset"
            className="w-full h-full object-cover opacity-45"
          />
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(8,13,28,0.85) 0%, rgba(8,13,28,0.75) 60%, rgba(8,13,28,0.95) 100%), radial-gradient(ellipse 60% 50% at 50% 0%, rgba(74,143,229,0.14), transparent 65%)",
            }}
          />
        </div>

        <div className="relative max-w-[1100px] mx-auto px-6 lg:px-12 py-24 lg:py-28 text-center">
          <Eyebrow>The Beginning</Eyebrow>
          <h2 className="mt-5 font-serif text-white text-[30px] sm:text-[42px] lg:text-[54px] leading-[1.05] tracking-[-0.015em]">
            Formation begins with a{" "}
            <span className="italic text-azure-soft">conversation</span>.
          </h2>
          <div className="mt-8 h-px w-16 bg-gold mx-auto" />

          <p className="mt-8 text-white/75 text-[15.5px] lg:text-[16.5px] leading-[1.9] max-w-2xl mx-auto">
            Every young person deserves guidance.
            <br />
            Every young person deserves someone who sees their potential.
            <br />
            <span className="text-azure-soft italic">Let&apos;s begin the journey.</span>
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              {...APPLY_PROPS}
              data-testid="ymg-final-apply"
              className="group inline-flex items-center justify-center gap-3 bg-azure hover:bg-[#5C9BEC] text-white px-8 py-4 text-[11px] tracking-[0.32em] uppercase font-medium transition-all"
            >
              Apply For Mentorship
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <a
              href={DISCOVERY_MAILTO}
              data-testid="ymg-final-discovery"
              className="group inline-flex items-center justify-center gap-3 border border-white/40 hover:border-gold hover:text-gold text-white px-8 py-4 text-[11px] tracking-[0.32em] uppercase font-medium transition-all"
            >
              Schedule A Discovery Call
              <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-400 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.8} />
            </a>
          </div>

          <div className="mt-10 text-white/45 text-[12px]">
            Questions? Email{" "}
            <a
              href={mailtoLink("me", "Youth Mentorship Inquiry")}
              data-testid="ymg-final-email"
              className="text-azure-soft hover:text-white underline underline-offset-4"
            >
              me@theiyare.com
            </a>
          </div>
        </div>
      </section>

      {/* MYI Certified Participant seal */}
      <section
        data-testid="myi-certified-seal-section"
        className="relative border-b border-white/5 bg-ink-deep"
      >
        <div className="max-w-[900px] mx-auto px-6 lg:px-12 py-16 lg:py-20 text-center">
          <div className="flex items-center gap-6 justify-center">
            <span
              aria-hidden="true"
              className="h-px flex-1 max-w-[180px]"
              style={{
                background:
                  "linear-gradient(to right, transparent, rgba(201,169,97,0.55), rgba(201,169,97,0.55))",
              }}
            />
            <img
              src={MYI_ICON}
              alt="MYI mark"
              data-testid="myi-seal-icon"
              className="w-14 h-14 lg:w-16 lg:h-16 rounded-full shadow-[0_0_36px_-6px_rgba(74,143,229,0.4)]"
            />
            <span
              aria-hidden="true"
              className="h-px flex-1 max-w-[180px]"
              style={{
                background:
                  "linear-gradient(to left, transparent, rgba(201,169,97,0.55), rgba(201,169,97,0.55))",
              }}
            />
          </div>

          <div className="mt-8 text-[10.5px] tracking-[0.42em] uppercase text-gold">
            MYI Certified Participant
          </div>
          <div className="mt-4 font-serif italic text-white text-[22px] lg:text-[26px] leading-[1.25] tracking-[-0.005em]">
            The Murabbi Youth Initiative
          </div>
          <div className="mt-6 flex items-center justify-center gap-4 text-[11px] tracking-[0.34em] uppercase text-azure-soft">
            <span>Character</span>
            <span aria-hidden="true" className="text-white/25">·</span>
            <span>Discipline</span>
            <span aria-hidden="true" className="text-white/25">·</span>
            <span>Purpose</span>
          </div>

          <div className="mt-10 flex items-center gap-6 justify-center">
            <span
              aria-hidden="true"
              className="h-px flex-1 max-w-[220px]"
              style={{
                background:
                  "linear-gradient(to right, transparent, rgba(201,169,97,0.4))",
              }}
            />
            <span className="text-[10px] tracking-[0.42em] uppercase text-white/40">
              An initiative of Iyare Omoruyi
            </span>
            <span
              aria-hidden="true"
              className="h-px flex-1 max-w-[220px]"
              style={{
                background:
                  "linear-gradient(to left, transparent, rgba(201,169,97,0.4))",
              }}
            />
          </div>
        </div>
      </section>

      {/* Powered by MYI band */}
      <section
        data-testid="myi-powered-by"
        className="relative border-b border-white/5 bg-ink"
      >
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-10 lg:py-12 grid sm:grid-cols-[auto_1fr] gap-6 items-center">
          <img
            src={MYI_ICON}
            alt="MYI mark"
            data-testid="myi-powered-by-icon"
            className="w-11 h-11 rounded-full"
          />
          <div>
            <div className="text-[10px] tracking-[0.42em] uppercase text-gold">
              Powered by
            </div>
            <div className="mt-2 font-serif text-white text-[18px] lg:text-[20px] tracking-[-0.005em]">
              The Murabbi Youth Initiative
            </div>
            <div className="mt-1 text-[12px] text-white/50">
              An initiative of{" "}
              <span className="italic text-azure-soft">Iyare Omoruyi</span>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <RelatedServices currentSlug="youth-mentorship-guidance" />
    </main>
  );
};

export default YouthMentorshipGuidance;
